import {
	BASE_PRECISION_EXP,
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import {
	disallowNegativeStringInput,
	roundToStepSizeIfLargeEnough,
} from '@drift/common';

interface Params {
	priceInputValue: string;
	baseSizeInputValueBigNum: BigNum;
	setPriceInputValue: (value: string) => void;
	setBaseSizeInputValue: (value: string) => void;
	setQuoteSizeInputValue: (value: string) => void;
	setIsQuoteSizeFromQuoteInput: (value: boolean) => void;
	isMarketOrder: boolean;
	markPriceToUse: BigNum;
	maxBaseSizeString: string;
	stepSize: number;
	openPositionSizeBigNum: BigNum;
	setCurrentSettings: (settings: {
		showClosePositionConfirmation: boolean;
	}) => void;
	showClosePositionConfirmation: boolean;
}

export default function useClosePositionModalHandlers({
	priceInputValue,
	baseSizeInputValueBigNum,
	setPriceInputValue,
	setBaseSizeInputValue,
	setQuoteSizeInputValue,
	setIsQuoteSizeFromQuoteInput,
	isMarketOrder,
	markPriceToUse,
	maxBaseSizeString,
	stepSize,
	openPositionSizeBigNum,
	setCurrentSettings,
	showClosePositionConfirmation,
}: Params) {
	/**
	 * Updates the price input state and the new corresponding quote size input state.
	 */
	const handlePriceInputChange = (value: string) => {
		const price = BigNum.fromPrint(value, PRICE_PRECISION_EXP);

		setQuoteSizeInputValue(
			price.scalarMul(baseSizeInputValueBigNum).toFixed(2)
		);

		setPriceInputValue(value);
	};

	/**
	 * Helper function to set the base / quote inputs to max possible.
	 */
	const setSizeInputValuesToMaxSize = (
		price: BigNum,
		updateQuote?: boolean
	) => {
		const newBaseSizeInputValue = roundToStepSizeIfLargeEnough(
			maxBaseSizeString,
			stepSize
		);
		setBaseSizeInputValue(newBaseSizeInputValue);

		if (updateQuote) {
			// Don't fix to 2 decimals, as it may result in an inaccurate base size value.
			const newQuoteSizeInputValue = price
				.scalarMul(openPositionSizeBigNum.abs())
				.printShort();
			setQuoteSizeInputValue(newQuoteSizeInputValue);
		}
	};

	/**
	 * Used for both the size input and position slider.
	 * Note that we don't update the quote size manually in this case,
	 * instead it is auto-calculated by a separate hook that is shared by
	 * both `ClosePositionModal` and `TpSlOrderFormPopup`.
	 */
	const handleBaseSizeInputChange = (value: string) => {
		setIsQuoteSizeFromQuoteInput(false);

		const price = BigNum.fromPrint(priceInputValue, PRICE_PRECISION_EXP);
		const isGreaterThanMaxSize = BigNum.fromPrint(value, BASE_PRECISION_EXP).gt(
			openPositionSizeBigNum.abs()
		);

		if (isGreaterThanMaxSize) {
			setSizeInputValuesToMaxSize(price);
			return;
		}

		const newBaseSizeInputValue = roundToStepSizeIfLargeEnough(value, stepSize);
		setBaseSizeInputValue(newBaseSizeInputValue);
	};

	/**
	 * Updates the quote size input state and the new corresponding base size input state.
	 */
	const handleQuoteSizeInputChange = (value: string) => {
		setIsQuoteSizeFromQuoteInput(true);

		const quoteSize = BigNum.fromPrint(value, QUOTE_PRECISION_EXP);
		const price = isMarketOrder
			? markPriceToUse
			: BigNum.fromPrint(priceInputValue, PRICE_PRECISION_EXP);

		const newBaseSize = quoteSize
			.scale(QUOTE_PRECISION, price.val)
			.shiftTo(BASE_PRECISION_EXP);

		const isGreaterThanMaxSize = newBaseSize.gt(openPositionSizeBigNum.abs());

		if (isGreaterThanMaxSize) {
			setSizeInputValuesToMaxSize(price, true);
			return;
		}

		const newBaseSizeInputValue = roundToStepSizeIfLargeEnough(
			newBaseSize.toNum().toString(),
			stepSize
		);
		setBaseSizeInputValue(newBaseSizeInputValue);
		setQuoteSizeInputValue(value);
	};

	const handleSizeInputChange = (
		value: string,
		inputSource: 'base' | 'quote'
	) => {
		const sanitizedValue = disallowNegativeStringInput(value);

		const updateFn =
			inputSource === 'base'
				? handleBaseSizeInputChange
				: handleQuoteSizeInputChange;

		updateFn(sanitizedValue);
	};

	const handlePositionSliderChange = (value: number) => {
		handleSizeInputChange(
			openPositionSizeBigNum.scale(value * 100, 10000).printShort(true),
			'base'
		);
	};

	const handleToggleShowClosePositionConfirmation = () => {
		setCurrentSettings({
			showClosePositionConfirmation: !showClosePositionConfirmation,
		});
	};

	return {
		handlePriceInputChange,
		handleSizeInputChange,
		handlePositionSliderChange,
		handleToggleShowClosePositionConfirmation,
	};
}
